import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const EditUserModal = ({ isOpen, user, onClose, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    nickname: '',
    role: '',
    is_active: true,
    is_pm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const roleOptions = [
    { value: 'global_administrator', label: 'Global Administrator', description: 'Highest-level access, same as Administrator' },
    { value: 'administrator', label: 'Administrator', description: 'Full system access' },
    { value: 'user', label: 'User', description: 'Standard access' },
    { value: 'view_only', label: 'View Only', description: 'Read-only access' }
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        nickname: user?.nickname || '',
        role: user?.role || 'user',
        is_active: user?.is_active ?? true,
        is_pm: user?.is_pm ?? false
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase?.from('user_profiles')?.update({
          first_name: formData?.first_name || null,
          last_name: formData?.last_name || null,
          nickname: formData?.nickname || null,
          role: formData?.role,
          is_active: formData?.is_active,
          is_pm: formData?.is_pm
        })?.eq('id', user?.id);

      if (updateError) throw updateError;
      onUserUpdated();
    } catch (err) {
      console.error('Error updating user:', err?.message);
      setError(err?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="UserCog" size={20} className="text-accent" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Edit User</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Icon name="User" size={18} className="text-accent" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{user?.full_name || 'Unnamed User'}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="First Name"
              type="text"
              placeholder="First name"
              value={formData?.first_name}
              onChange={(e) => setFormData(prev => ({ ...prev, first_name: e?.target?.value }))}
            />
            <Input
              label="Last Name"
              type="text"
              placeholder="Last name"
              value={formData?.last_name}
              onChange={(e) => setFormData(prev => ({ ...prev, last_name: e?.target?.value }))}
            />
          </div>

          <Input
            label="Nickname"
            type="text"
            placeholder="Display nickname (used in PM assignments & reports)"
            value={formData?.nickname}
            onChange={(e) => setFormData(prev => ({ ...prev, nickname: e?.target?.value }))}
          />

          <Select
            label="Role"
            options={roleOptions}
            value={formData?.role}
            onChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
            required
          />

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-foreground">Account Status</p>
              <p className="text-xs text-muted-foreground">Active users can access the system</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, is_active: !prev?.is_active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData?.is_active ? 'bg-accent' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData?.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-foreground">Project Manager (PM)</p>
              <p className="text-xs text-muted-foreground">Designates this user as a Project Manager</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, is_pm: !prev?.is_pm }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData?.is_pm ? 'bg-accent' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData?.is_pm ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
              className="flex-1"
              loading={loading}
              disabled={loading}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;